import { gql } from "@apollo/client";
import { ApolloClientQuery } from "./_setup";
import { createLogSession } from "../restful/logs/logs";

const VERIFY_ACCESS_TOKEN = gql`
    mutation verifyAccessToken($accessToken: String) {
        verifyAccessToken(accessToken: $accessToken)
        {
            ...on AuthPayload{
                login
                accessToken
                userid
            }
            ...on BaseError {
                errKey
                errMsg
            }
        }
    }
`

export async function verifyAccessTokenMutation(token) {

    const variables = {
        accessToken: token
    };
    const execute = await ApolloClientQuery(VERIFY_ACCESS_TOKEN, variables, true);
    return execute;
}

const LOGIN = gql`
    mutation login($input: AuthInput) {
        login(input: $input)
        {
            ...on AuthPayload{
                login
                accessToken
                userid
            }
            ...on BaseError {
                errKey
                errMsg
            }
        }
    }
`

export async function loginMutation(input) {

    const variables = {
        input: {
            login: input.login || null,
            password: input.password || null,
            type: input.type || null,
            deviceId: input?.deviceId || null,
            ip: input?.ip || null,
            // platform: "web",
        }
    };
    const execute = await ApolloClientQuery(LOGIN, variables, true);
    return execute;
}

const GET_REGISTRATION_SETTING = gql`
    query getRegistrationSetting($filter: RegistrationSettingSearchInput) {
        getRegistrationSetting(filter: $filter) {
            ... on RegistrationSettingArray {
                data {
                    id
                    position
                    questionnaireFormSettingId
                    fieldList{
                        isRequired
                        isConfirmRequired
                        label
                        fieldName
                        fieldType
                        category
                        optionValue{
                            label
                            value
                        }
                        isUsedForLogin
                        minCharacterLimit
                        maxCharacterLimit
                        lowercaseOnly
                    }
                }
            }
        }
    }
`;

export async function getRegistrationSettingQuery(mode) {
    const variables = {
        filter: {
            platform: "web",
            mode: mode === "demo" ? mode : 'live',
            hasPosition: true
        },
    };

    return await ApolloClientQuery(GET_REGISTRATION_SETTING, variables, false);
}

const USER_REGISTER = gql`
    mutation register($input: RegistrationInput) {
        register(input: $input) {
            ... on User {
                id
                _id
                email
                domain
                username
                firstName
                middleName
                lastName
                fullName
                age
                identificationNo
                dateOfBirth
                gender
                comment
                countryCode
                mobileNo
                completedMobileNo
                profileImage
                affiliateCode
                pspUserId
                language
                newsletter
                verifiedEmail
                address
                city
                postalCode
                state
                country
                status
                desk {name}
                role {name}
                referrer {
                    _id
                    fullName
                }
                salesRep {
                    _id
                    fullName
                }
                eWallet{_id}
                clientAccountType
                isLoginEnabled
                isTestAccount
                latestFollowUp{followUpDateTime}
                previousFollowUp{lastFollowUp}
                account
                createdAt
                updatedAt
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`;

export async function registerMutation(input) {

    const variables = {
        input: {
            ...input,
        }
    };
    const execute = await ApolloClientQuery(USER_REGISTER, variables, true);
    return execute;
}

const FORGET_PASSWORD = gql`
    mutation forgetPassword($input: ForgetPasswordInput) {
        forgetPassword(input: $input) {
            ... on UserMessage {
                msg
                msgKey
            }
            ... on BaseError {
                errKey
                errMsg
            }
        }
    }
`;

export async function forgetPassword(input) {
    const session = createLogSession("Auth", "Password", "forgetPassword");
    session.create({ description: "Requesting password reset", logData: { input } });

    const variables = {
        input: {
            input: input
        }
    };
    try {
        const execute = await ApolloClientQuery(FORGET_PASSWORD, variables, true);
        const err = execute?.forgetPassword?.__typename === "BaseError" ? execute?.forgetPassword : null;
        session.create({ description: err ? "Password reset request failed" : "Password reset requested", status: err ? "error" : "info", logData: err ? { error: err, input } : { input } });
        return execute;
    } catch (e) {
        session.create({ description: "Password reset request failed", status: "error", logData: { input, error: e } });
        throw e;
    }
}

const RESET_PASSWORD = gql`
    mutation resetPassword($input: ResetPassswordInput) {
        resetPassword(input: $input) {
            ... on UserMessage {
                msg
                msgKey
            }
            ... on BaseError {
                errKey
                errMsg
            }
        }
    }
`;

export async function resetPassword(key, newPassword) {
    const session = createLogSession("Auth", "Password", "resetPassword");
    session.create({ description: "Resetting password", logData: { resetLink: key } });

    const variables = {
        input: {
            resetLink: key,
            newPassword: newPassword
        }
    };
    try {
        const execute = await ApolloClientQuery(RESET_PASSWORD, variables, true);
        const err = execute?.resetPassword?.__typename === "BaseError" ? execute?.resetPassword : null;
        session.create({ description: err ? "Password reset failed" : "Password reset completed", status: err ? "error" : "info", logData: err ? { error: err, resetLink: key } : { resetLink: key } });
        return execute;
    } catch (e) {
        session.create({ description: "Password reset failed", status: "error", logData: { resetLink: key, error: e } });
        throw e;
    }
}

const GET_LOGIN_HISTORY = gql`
    query getLoginHistory($filter: LoginHistorySearchInput,$limit:Int,$offset:Int,$orderBy:String) {
        getLoginHistory(filter: $filter,limit:$limit,offset:$offset,orderBy:$orderBy)
        {
            ...on LoginHistory{
                total
                record{
                   loginHistory{
                       loginTime
                       deviceId
                       ipAddress
                       browser
                       status
                   }
                }
            }
            ...on BaseError {
                errKey
                errMsg
            }
        }
    }
`

export async function getLoginHistory(userid, limit, offset, orderBy) {

    const variables = {
        filter: {
            userid: userid
        },
        limit: limit,
        offset: offset || 0,
        orderBy: orderBy,
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    const execute = await ApolloClientQuery(GET_LOGIN_HISTORY, variables, false);
    return execute;
}

const VERIFY_EMAIL = gql`
    mutation verifyEmail($input: VerifyEmailInput) {
        verifyEmail(input: $input){
            ... on User {
                id
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`

export async function verifyEmail(input) {
    const variables = {
        input: input,
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    const execute = await ApolloClientQuery(VERIFY_EMAIL, variables, true);
    return execute;
}

const VERIFY_CRYPTO_ADDRESS = gql`
    mutation verifyCryptoAddress($email: String, $transactionNo: String) {
        verifyCryptoAddress(email: $email, transactionNo: $transactionNo){
            ... on EWalletWithdrawal {
                _id
            }
            ... on BaseError {
                errKey
                errMsg
            }
            ... on CrmTaskApproval {
                msg
            }
        }
    }
`

export async function verifyCryptoAddress(email, transactionNo) {
    const session = createLogSession("Auth", "Crypto", "verifyCryptoAddress");
    session.create({ description: "Verifying crypto address", logData: { email, transactionNo } });

    const variables = {
        email: email,
        transactionNo: transactionNo,
    };
    try {
        const execute = await ApolloClientQuery(VERIFY_CRYPTO_ADDRESS, variables, true);
        const err = execute?.verifyCryptoAddress?.__typename === "BaseError" ? execute?.verifyCryptoAddress : null;
        session.create({ description: err ? "Crypto address verification failed" : "Crypto address verified", status: err ? "error" : "info", logData: err ? { error: err, email, transactionNo } : { email, transactionNo } });
        return execute;
    } catch (e) {
        session.create({ description: "Crypto address verification failed", status: "error", logData: { email, transactionNo, error: e } });
        throw e;
    }
}

const GO_LIVE = gql`
    mutation goLive($id: String!, $input: UserInput) {
        goLive(id: $id, input: $input){
            ... on User {
                _id
            }
            ... on BaseError {
                errKey
                errMsg
            }
            ... on CrmTaskApproval {
                msg
            }
        }
    }
`

export async function goLive(id, input) {
    const session = createLogSession("Auth", "Account", "goLive");
    session.create({ description: "Submitting go-live request", involveUserId: id, logData: { input } });

    const variables = {
        id: id,
        input: input,
    };
    try {
        const execute = await ApolloClientQuery(GO_LIVE, variables, true);
        const err = execute?.goLive?.__typename === "BaseError" ? execute?.goLive : null;
        session.create({ description: err ? "Go-live request failed" : "Go-live request submitted", status: err ? "error" : "info", involveUserId: id, logData: err ? { error: err, input } : { input } });
        return execute;
    } catch (e) {
        session.create({ description: "Go-live request failed", status: "error", involveUserId: id, logData: { input, error: e } });
        throw e;
    }
}

const RESEND_VERIFICATION_EMAIL = gql`
    mutation resendVerificationEmail($input: ResendVerificationEmailInput) {
        resendVerificationEmail(input: $input){
             ... on ResendVerificationResponse {
                        status
                    }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`

export async function resendVerificationEmail(input) {
    const session = createLogSession("Auth", "Verification", "resendVerificationEmail");
    session.create({ description: "Resending verification email", logData: { input } });

    const variables = {
        input: input,
    };
    try {
        const execute = await ApolloClientQuery(RESEND_VERIFICATION_EMAIL, variables, true);
        const err = execute?.resendVerificationEmail?.__typename === "BaseError" ? execute?.resendVerificationEmail : null;
        session.create({ description: err ? "Failed to resend verification email" : "Verification email resent", status: err ? "error" : "info", logData: err ? { error: err, input } : { input } });
        return execute;
    } catch (e) {
        session.create({ description: "Failed to resend verification email", status: "error", logData: { input, error: e } });
        throw e;
    }
}

const START_PROVIDER_AUTH = gql`
    mutation startProviderAuth($input: StartProviderAuthInput!) {
        startProviderAuth(input: $input) {
            ... on StartProviderAuthPayload {
                authUrl
                authRequestId
                retryAfterSeconds
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`

export async function startProviderAuthMutation(input) {
    const session = createLogSession("Auth", "Provider", "startProviderAuth");
    session.create({ description: "Starting provider authentication", logData: { provider: input?.provider, flow: input?.flow, registerType: input?.registerType } });

    const variables = {
        input: {
            provider: input?.provider || null,
            flow: input?.flow || null,
            platform: input?.platform || "web",
            userType: input?.userType || "client",
            registerType: input?.registerType || null,
            phoneNumber: input?.phoneNumber || null,
            clientOrigin: input?.clientOrigin || null,
            ipAddress: input?.ipAddress || null,
            userAgent: input?.userAgent || null,
        },
    };
    try {
        const execute = await ApolloClientQuery(START_PROVIDER_AUTH, variables, true);
        const err = execute?.startProviderAuth?.__typename === "BaseError" ? execute?.startProviderAuth : null;
        session.create({ description: err ? "Provider authentication failed" : "Provider authentication started", status: err ? "error" : "info", logData: err ? { error: err, provider: input?.provider, flow: input?.flow } : { provider: input?.provider, flow: input?.flow } });
        return execute;
    } catch (e) {
        session.create({ description: "Provider authentication failed", status: "error", logData: { provider: input?.provider, flow: input?.flow, error: e } });
        throw e;
    }
}

const COMPLETE_PROVIDER_CALLBACK = gql`
    mutation completeProviderCallback($input: CompleteProviderCallbackInput!) {
        completeProviderCallback(input: $input) {
            ... on CompleteProviderCallbackPayload {
                nextAction
                accessToken
                refreshToken
                signupToken
                registerType
                linked
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`

export async function completeProviderCallbackMutation(input) {
    const variables = {
        input: {
            provider: input?.provider || null,
            authRequestId: input?.authRequestId || null,
            code: input?.code || null,
            state: input?.state || null,
            otpCode: input?.otpCode || null,
            telegramData: input?.telegramData || null,
            ipAddress: input?.ipAddress || null,
            userAgent: input?.userAgent || null,
        },
    };
    const execute = await ApolloClientQuery(COMPLETE_PROVIDER_CALLBACK, variables, true);
    return execute;
}

const COMPLETE_PROVIDER_CALLBACK_EXCHANGE = gql`
    mutation completeProviderCallbackExchange($input: CompleteProviderCallbackExchangeInput!) {
        completeProviderCallbackExchange(input: $input) {
            ... on CompleteProviderCallbackPayload {
                nextAction
                accessToken
                refreshToken
                signupToken
                registerType
                linked
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`

export async function completeProviderCallbackExchangeMutation(input) {
    const session = createLogSession("Auth", "Provider", "completeProviderCallbackExchange");
    session.create({ description: "Completing provider callback exchange", logData: {} });

    const variables = {
        input: {
            exchangeCode: input?.exchangeCode || null,
        },
    };
    try {
        const execute = await ApolloClientQuery(
            COMPLETE_PROVIDER_CALLBACK_EXCHANGE,
            variables,
            true,
        );
        const err = execute?.completeProviderCallbackExchange?.__typename === "BaseError" ? execute?.completeProviderCallbackExchange : null;
        session.create({ description: err ? "Provider callback exchange failed" : "Provider callback exchange completed", status: err ? "error" : "info", logData: err ? { error: err } : {} });
        return execute;
    } catch (e) {
        session.create({ description: "Provider callback exchange failed", status: "error", logData: { error: e } });
        throw e;
    }
}

const COMPLETE_PROVIDER_LINK = gql`
    mutation completeProviderLink($input: CompleteProviderLinkInput!) {
        completeProviderLink(input: $input) {
            ... on CompleteProviderLinkPayload {
                login
                userid
                username
                accessToken
                refreshToken
                linked
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`

export async function completeProviderLinkMutation(input) {
    const session = createLogSession("Auth", "Provider", "completeProviderLink");
    session.create({ description: "Completing provider linking", logData: { provider: input?.provider } });

    const variables = {
        input: {
            provider: input?.provider || null,
            signupToken: input?.signupToken || null,
            password: input?.password || null,
            ipAddress: input?.ipAddress || null,
            userAgent: input?.userAgent || null,
        },
    };
    try {
        const execute = await ApolloClientQuery(COMPLETE_PROVIDER_LINK, variables, true);
        const err = execute?.completeProviderLink?.__typename === "BaseError" ? execute?.completeProviderLink : null;
        session.create({ description: err ? "Provider linking failed" : "Provider linked", status: err ? "error" : "info", logData: err ? { error: err, provider: input?.provider } : { provider: input?.provider } });
        return execute;
    } catch (e) {
        session.create({ description: "Provider linking failed", status: "error", logData: { provider: input?.provider, error: e } });
        throw e;
    }
}
const COMPLETE_PROVIDER_SIGNUP = gql`
    mutation completeProviderSignup($input: CompleteProviderSignupInput!) {
        completeProviderSignup(input: $input) {
            ... on CompleteProviderSignupPayload {
                login
                userid
                username
                accessToken
                refreshToken
                emailVerificationRequired
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`

export async function completeProviderSignupMutation(input) {
    const variables = {
        input: {
            signupToken: input?.signupToken || null,
            registrationInput: input?.registrationInput || null,
        },
    };
    const execute = await ApolloClientQuery(COMPLETE_PROVIDER_SIGNUP, variables, true);
    return execute;
}

const GET_PROVIDER_SIGNUP_DRAFT = gql`
  query GetProviderSignupDraft($input: GetProviderSignupDraftInput!) {
    getProviderSignupDraft(input: $input) {
      __typename
      ... on ProviderSignupDraftPayload {
        provider
        flow
        email
        emailVerified
        username
        fullName
        firstName
        lastName
        phoneNumber
      }
      ... on BaseError {
        errKey
        errMsg
      }
    }
  }
`;

export async function getProviderSignupDraftQuery(input) {
    const variables = { input };
    return await ApolloClientQuery(GET_PROVIDER_SIGNUP_DRAFT, variables, false);
}



